import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, Music, Calendar, DollarSign, TrendingUp, 
  Clock, Star, BarChart3 
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';
import AdminLayout from '../components/AdminLayout';

const COLORS = ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for user to load before checking role
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { title: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
    { title: 'Total Artists', value: stats?.total_artists || 0, icon: Music, color: 'text-violet-400' },
    { title: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, color: 'text-green-400' },
    { title: 'Total Revenue', value: `$${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'text-yellow-400' },
    { title: 'Pending Artists', value: stats?.pending_artists || 0, icon: Clock, color: 'text-orange-400' },
    { title: 'Bookings This Month', value: stats?.bookings_this_month || 0, icon: TrendingUp, color: 'text-pink-400' },
  ];

  const topArtistsData = stats?.top_artists?.map(a => ({
    name: a.bio?.substring(0, 15) + '...' || 'Artist',
    bookings: a.total_bookings,
    revenue: a.total_revenue || 0,
    rating: a.rating || 0
  })) || [];

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold gradient-text">Analytics Dashboard</h1>
          <p className="text-white/60">Platform statistics and insights</p>
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, idx) => (
            <Card key={idx} className="glass-default border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold">{stat.value}</p>
                  </div>
                  <stat.icon className={`w-12 h-12 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Revenue This Month Card */}
        <Card className="glass-default border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="text-green-400" />
              Revenue This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-green-400">
              ${(stats?.revenue_this_month || 0).toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Artists Chart */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="text-violet-400" />
                Top Artists by Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topArtistsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topArtistsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" tick={{ fontSize: 12 }} />
                    <YAxis stroke="rgba(255,255,255,0.5)" />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'rgba(30,30,50,0.9)', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="bookings" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-white/60">
                  No artist data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Artists by Rating */}
          <Card className="glass-default border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="text-yellow-400" />
                Artist Ratings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topArtistsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={topArtistsData}
                      dataKey="rating"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, rating }) => `${name}: ${(rating ?? 0).toFixed(1)}`}
                    >
                      {topArtistsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-white/60">
                  No rating data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Bookings Table */}
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="text-blue-400" />
              Recent Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Booking ID</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">User</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Artist</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-white/60 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recent_bookings?.map((booking, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 font-mono text-sm">{booking.booking_id?.slice(-8)}</td>
                      <td className="py-3 px-4">{booking.user_name}</td>
                      <td className="py-3 px-4">{booking.artist_name}</td>
                      <td className="py-3 px-4">{booking.event_date}</td>
                      <td className="py-3 px-4 text-green-400">${booking.total_cost?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          booking.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                          booking.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {booking.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!stats?.recent_bookings?.length && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-white/60">
                        No recent bookings
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
